import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';

/**
 * Export data to Excel
 * @param {Array} data - Array of objects to export
 * @param {string} fileName - Name of the file
 * @param {string} sheetName - Name of the sheet
 */
export const exportToExcel = (data, fileName, sheetName = 'Sheet1') => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, `${fileName}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
};

/**
 * Export data to PDF
 * @param {Object} options - PDF export options
 * @param {string} options.title - Document title
 * @param {Array} options.head - Table headers [[col1, col2, ...]]
 * @param {Array} options.body - Table data [[val1, val2, ...]]
 * @param {string} options.fileName - Name of the file
 */
export const exportToPDF = ({ title, head, body, fileName }) => {
  const doc = new jsPDF();

  doc.setFontSize(18);
  doc.text(title, 14, 22);
  doc.setFontSize(11);
  doc.text(`Generated on: ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, 14, 30);

  autoTable(doc, {
    head,
    body,
    startY: 35,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [15, 23, 42] },
    alternateRowStyles: { fillColor: [249, 250, 251] },
  });

  doc.save(`${fileName}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
};

/**
 * Formatters for specific reports
 */
export const reportFormatters = {
  users: (users) => users.map(user => ({
    'Name': user.personalInformation?.fullName || user.personalDetails?.name || user.displayName || 'N/A',
    'Mobile': user.personalInformation?.mobileNumber || user.personalDetails?.mobile || user.mobileNumber || 'N/A',
    'Email': user.personalInformation?.email || user.personalDetails?.email || user.email || 'N/A',
    'Type': user.accountSecurity?.accountType || 'Guest',
    'Purpose': user.accountSecurity?.detailedPurpose || 'N/A',
    'Joined Date': format(new Date(user.metadata?.createdAt || user.createdAt || new Date()), 'MMM dd, yyyy'),
    'Shake Balance': user.inventory?.remainingShakes || 0,
    'Referred By': user.accountSecurity?.referredByName || 'N/A'
  })),

  orders: (orders) => orders.map(order => ({
    'Order ID': order.orderId || order.id || 'N/A',
    'Date': order.timestamp || order.createdAt ? format(new Date(order.timestamp || order.createdAt), 'MMM dd, yyyy HH:mm') : 'N/A',
    'User': order.userName || 'N/A',
    'Mobile': order.userMobile || 'N/A',
    'Type': order.transaction?.type || 'N/A',
    'Amount': order.summary?.grandTotalAmount || 0,
    'VP': order.summary?.totalOrderVP || 0,
    'Shake Quantity': order.summary?.shakeBalanceChange || 0,
    'Payment': order.transaction?.paymentMethod || 'N/A',
    'Status': order.transaction?.status || 'completed'
  })),

  inventory: (products) => products.map(product => ({
    'Product': product.productName,
    'Code': product.productCode,
    'Category': product.categoryName,
    'Stock': product.stockCount || 0,
    'Price': product.productPrice || 0,
    'VP': product.valuePoint || 0,
    'Status': (product.stockCount || 0) > 10 ? 'In Stock' : (product.stockCount || 0) > 0 ? 'Low Stock' : 'Out of Stock'
  })),

  transactions: (txns) => txns.map(txn => ({
    'Date': format(new Date(txn.timestamp || txn.createdAt), 'MMM dd, yyyy HH:mm'),
    'Type': txn.type || 'N/A',
    'Product': txn.productName || 'N/A',
    'Quantity': txn.quantity || 0,
    'Amount': txn.totalAmount || 0,
    'VP': txn.totalValuePoints || 0,
    'Note': txn.note || 'N/A'
  }))
};
